import React from "react";
import Modal from "../../../components/ui/Modal/Modal";
import { useMarkTaxInvoiceIssued } from "../../../api/tax-invoice/use-tax-invoice";
import {
  formatDateTime,
  formatPrice,
  orderClassificationText,
} from "../../../utils";

function paidTypeLabel(paidType) {
  switch (paidType) {
    case "ACCOUNT":
      return "무통장 입금";
    case "CREDIT":
      return "신용카드";
    default:
      return paidType ? String(paidType) : "-";
  }
}

function publicationStatusLabel(order) {
  return (
    order?.taxInvoicePublicationStatus ??
    order?.taxInvoiceStatus ??
    order?.taxInvoiceRequestStatus ??
    "발행대기"
  );
}

function publicationProcessedAt(order) {
  const at =
    order?.taxInvoicePublishedAt ??
    order?.publicationProcessedAt ??
    order?.taxInvoiceProcessedAt;
  return at ? formatDateTime(at) : "";
}

function shippingAddressText(order) {
  const sa =
    order?.shippingAddress ?? order?.productOrderParent?.shippingAddress;
  if (sa?.address != null || sa?.addressDetail != null) {
    const zip = sa.zipCode ?? sa.postalCode;
    const head = zip ? `(${zip}) ` : sa.address ? `(${sa.address}) ` : "";
    const body = sa.addressDetail ?? sa.address ?? "";
    const combined = `${head}${body}`.trim();
    return combined || "-";
  }
  if (order?.deliveryAddress) {
    return String(order.deliveryAddress);
  }
  if (order?.hospital?.address) {
    return String(order.hospital.address);
  }
  return "-";
}

function paymentInfoText(order) {
  const pi = order?.paymentInformation;
  if (pi) {
    const line = [pi.bankName, pi.bankHolderName, pi.bankAccountNo]
      .filter(Boolean)
      .join(" ");
    if (line) {
      return line;
    }
  }
  const pop = order?.productOrderParent;
  const line = [
    order?.bankName ?? pop?.bankName,
    order?.bankHolderName ?? pop?.bankHolderName,
    order?.bankAccountNo ?? order?.bankAccount ?? pop?.bankAccountNo,
  ]
    .filter(Boolean)
    .join(" ");
  if (line) {
    return line;
  }
  return order?.bankHolderBizNo != null && order.bankHolderBizNo !== ""
    ? String(order.bankHolderBizNo)
    : "-";
}

function paymentStatusText(order) {
  // const status = order?.status ?? order?.productOrderParent?.status;
  // if (status) {
  //   return productOrderStatusText(status);
  // }
  const paidAt =
    order?.paidCompletedAt ?? order?.productOrderParent?.paidCompletedAt;
  return paidAt != null ? "결제완료" : "-";
}

function ordererDisplay(order) {
  const name = order?.hospital?.name ?? "";
  const email = order?.hospital?.email ?? "";
  if (name && email) {
    return `${name} (${email})`;
  }
  return name || email || "-";
}

function representativePhones(order) {
  const ceo = order?.hospital?.ceoPhone ?? "";
  const manager = order?.hospital?.managerPhone ?? "";
  if (ceo && manager) {
    return `${ceo} (${manager})`;
  }
  return ceo || manager || "-";
}

function paidCompletedAtText(order) {
  const at =
    order?.paidCompletedAt ?? order?.productOrderParent?.paidCompletedAt;
  return at ? formatDateTime(at) : "";
}

function resolveProductOrderParentId(order) {
  return (
    order?.productOrderParent?.id ??
    order?.productOrderParentId ??
    order?.productOrder?.parentId ??
    order?.orderSummary?.productOrderParentId ??
    order?.id
  );
}

const TaxInvoiceModal = ({ order }) => {
  const { mutate: markIssued, isLoading: isMarkingIssued } =
    useMarkTaxInvoiceIssued();
  const netAmount = (order?.paidPrice ?? 0) - (order?.deliveryFee ?? 0);
  const orderPhone =
    order?.hospital?.contact ?? order?.hospital?.phone ?? order?.phone ?? "";
  const productOrderParentId = resolveProductOrderParentId(order);

  const onMarkIssued = () => {
    if (!productOrderParentId) {
      return;
    }
    markIssued({ productOrderParentId });
  };

  return (
    <div className="py-1">
      <div className="flex border-b pb-2 items-center justify-between">
        <h1 className="px-5 text-sm font-bold">세금계산서 발행</h1>
        <div className="flex px-5">
          <Modal
            maxWidth="max-w-[24rem]"
            submitText="발행완료"
            onSubmit={onMarkIssued}
            description={
              <div className="px-1 text-[11px]">
                세금계산서가 발행되었습니까?
                <br />
                1. 해당 기능은 수동 기능입니다. 세금계산서가 실제 발급이 완료될
                경우에
                <br />
                [발행완료] 처리를 해주시기바랍니다.
                <br />
                2. 발행완료 할 경우, 취소가 불가능하며 회원에게 알림이
                발송됩니다.
                <br />
              </div>
            }
          >
            <button
              type="button"
              disabled={!productOrderParentId || isMarkingIssued}
              className="bg-primary-blue disabled:bg-[#9ca3af] disabled:border-[#9ca3af] mr-2 border w-[4.5rem] rounded-[4px] py-1 border-primary-blue text-white"
            >
              발행완료
            </button>
          </Modal>

          <button className="border w-[4.5rem] rounded-[4px] border-black">
            취소
          </button>
        </div>
      </div>
      <div className="border-b-8 mx-5 py-4">
        <div className="flex w-full">
          <div className="border p-1.5 border-primary-grey bg-input-grey w-32">
            신청처리 현황
          </div>
          <input
            type="text"
            value={publicationStatusLabel(order)}
            readOnly
            className="border-primary-grey border border-l-0 w-[83%] p-1.5 bg-white"
            disabled
          />
        </div>
        <div className="flex w-full">
          <div className="border p-1.5 border-t-0 border-primary-grey bg-input-grey w-32">
            발행 처리 일시
          </div>
          <input
            type="text"
            value={publicationProcessedAt(order)}
            readOnly
            className="border-primary-grey border-t-0 border border-l-0 w-[83%] p-1.5 bg-white"
            disabled
          />
        </div>
      </div>
      <div className="px-5 pt-4">
        <h1 className="font-bold text-[13px]">주문 정보</h1>
        <div className="mt-2">
          <div className="flex">
            <div className="border p-1.5 border-primary-grey bg-input-grey w-24">
              주문번호
            </div>
            <input
              type="text"
              value={order?.orderNo ?? "-"}
              readOnly
              className="border-primary-grey w-[32.5%] border border-r-0 border-l-0 p-1.5 bg-white"
              disabled
            />
            <div className="border p-1.5 border-primary-grey bg-input-grey w-24">
              주문구분
            </div>
            <input
              type="text"
              value={orderClassificationText(order?.type)}
              readOnly
              className="border-primary-grey border border-l-0 w-[32.5%] p-1.5 bg-white"
              disabled
            />
          </div>
          <div className="flex">
            <div className="border p-1.5 border-t-0 border-primary-grey bg-input-grey w-24">
              주문일시
            </div>
            <input
              type="text"
              value={order?.orderAt ? formatDateTime(order?.orderAt) : "-"}
              readOnly
              className="border-primary-grey border-t-0 w-[32.5%] border border-r-0 border-l-0 p-1.5 bg-white"
              disabled
            />
            <div className="border p-1.5 border-t-0 border-primary-grey bg-input-grey w-24">
              주문자
            </div>
            <input
              type="text"
              value={ordererDisplay(order)}
              readOnly
              className="border-primary-grey flex flex-wrap border-t-0 border border-l-0 w-[32.5%] p-1.5 bg-white"
              disabled
            />
          </div>
          <div className="flex">
            <div className="border flex items-center p-1.5 border-t-0 border-primary-grey bg-input-grey w-24">
              전화번호
            </div>
            <input
              type="text"
              value={orderPhone || "-"}
              readOnly
              className="border-primary-grey border-t-0 w-[32.5%] border border-r-0 border-l-0 p-1.5 bg-white"
              disabled
            />
            <div className="border p-1.5 border-t-0 border-primary-grey bg-input-grey w-24">
              대표자/
              <br />
              담당자 연락처
            </div>
            <input
              type="text"
              value={representativePhones(order)}
              readOnly
              className="border-primary-grey border-t-0 border border-l-0 w-[32.5%] p-1.5 bg-white"
              disabled
            />
          </div>
          <div className="flex w-full">
            <div className="border p-1.5 border-t-0 border-primary-grey bg-input-grey w-[96.5px]">
              배송지
            </div>
            <input
              type="text"
              value={shippingAddressText(order)}
              readOnly
              className="border-primary-grey border-t-0 border border-l-0 w-[82.5%] p-1.5 bg-white"
              disabled
            />
          </div>
        </div>
        <div className="mt-4">
          <h1 className="font-bold mb-2 text-[13px]">결제 정보</h1>
          <div className="flex">
            <div className="border flex items-center p-1.5 border-primary-grey bg-input-grey w-[7.55rem]">
              결제상태
            </div>
            <input
              type="text"
              value={paymentStatusText(order)}
              readOnly
              className="border-primary-grey w-[9.5rem] border border-r-0 border-l-0 p-1.5 bg-white"
              disabled
            />
            <div className="border p-1.5 border-primary-grey bg-input-grey w-24">
              결제수단
            </div>
            <input
              type="text"
              value={paidTypeLabel(
                order?.paidType ?? order?.productOrderParent?.paidType
              )}
              readOnly
              className="border-primary-grey border border-l-0 w-[33.2%] p-1.5 bg-white"
              disabled
            />
          </div>
          <div className="flex w-full">
            <div className="border p-1.5 border-t-0 border-primary-grey bg-input-grey w-32">
              결제정보
            </div>
            <input
              type="text"
              value={paymentInfoText(order)}
              readOnly
              className="border-primary-grey border-t-0 border border-l-0 w-[83%] p-1.5 bg-white"
              disabled
            />
          </div>
          <div className="flex w-full">
            <div className="border p-1.5 border-t-0 border-primary-grey bg-input-grey w-32">
              결제금액
            </div>
            <input
              type="text"
              value={`${formatPrice(netAmount)}원`}
              readOnly
              className="border-primary-grey text-secondary-red border-t-0 border border-l-0 w-[83%] p-1.5 bg-white"
              disabled
            />
          </div>
          <div className="flex w-full">
            <div className="border p-1.5 border-t-0 border-primary-grey bg-input-grey w-32">
              승인일시(입금확인)
            </div>
            <input
              type="text"
              value={paidCompletedAtText(order)}
              readOnly
              className="border-primary-grey border-t-0 border border-l-0 w-[83%] p-1.5 bg-white"
              disabled
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxInvoiceModal;
